"use client";

import { useState } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { z } from "zod";
import { VersionsSchema } from "@/schemas/versions.schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  VersionsFormData,
  VersionsFormSchema,
} from "@/schemas/versions-form.schema";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import GlobalCssStrings from "@/app/globalCssStrings";
import { ToggleInput, ToggleLabel, ToggleSpan } from "../ui/toggle";
import { CreateVersion } from "@/services/versions.service";
import Dropdown from "../ui/dropdown";
import { Dropzone, DropzoneContent, DropzoneEmptyState } from "../ui/shadcn-io/dropzone";
import { uploadFile } from "@/services/fileUpload.service";

interface IProps {
  updateData: () => void;
  versions: z.infer<typeof VersionsSchema>[];
  onClose?: () => void;
}

export default function VersionsModal(props: IProps) {
  const [open, setOpen] = useState(false);
  const [isSubVersion, setIsSubVersion] = useState(false);

  const form = useForm<z.infer<typeof VersionsFormSchema>>({
    resolver: zodResolver(VersionsFormSchema),
    defaultValues: {
      Title: "",
      ParentVersionID: "",
      FileName: "",
    },
  });

  const createVersion = async (data: z.infer<typeof VersionsFormSchema>) => {

    if (!isSubVersion) {
      data.ParentVersionID = "";
    } else {
      // For some reason, if we don't click the dropdown, it doesn't register the current default selected value
      if (data.ParentVersionID == "") {
        const documentElement = document.getElementById("parentVersion") as HTMLInputElement;
        data.ParentVersionID = documentElement.value;
      }
    }

    const formData = VersionsFormData(data);

    let result = null;
    try {
      console.log("Creating version with data: ", JSON.stringify(formData));
      result = await CreateVersion(formData);
      closeModal();
    } catch (error) {
      console.log("Creation Failure: ", error);
    }
    console.log("Result: ", result);
  };

  const formErrorHandler = (data: unknown) => {
    console.log("Form Failure: ", data);
  };

  const [files, setFiles] = useState<File[]>([]);
  const handleDrop = (acceptedFiles: File[]) => {
    setFiles(acceptedFiles);

    uploadFile(acceptedFiles[0])
      .then(uploadSuccess)
      .catch((error) => {
        console.error("File upload failed:", error);
      });
  };

  const uploadSuccess = (fileName: string) => {
    form.setValue("FileName", fileName);
  }

  const closeModal = () => {
    setOpen(false);
    props?.onClose?.();
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="cursor-pointer">Add new version</Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogTitle>Add new version</DialogTitle>
        <div>
          <div className="">
            <ToggleLabel className="w-xs">
              Parent Version
              <ToggleInput
                className="w-xs"
                checked={isSubVersion}
                onChange={(_e) => setIsSubVersion(!isSubVersion)}
              ></ToggleInput>
              <ToggleSpan />
              Sub Version
            </ToggleLabel>
          </div>
          <Dropzone onDrop={handleDrop} onError={console.error} src={files}>
            <DropzoneEmptyState />
            <DropzoneContent />
          </Dropzone>
          <Form {...form}>
            <form
              autoComplete="off"
              className="grid"
              onSubmit={form.handleSubmit(createVersion, formErrorHandler)}
            >
              <FormField
                control={form.control}
                name="Title"
                render={({ field }) => (
                  <FormItem className="mb-2">
                    <FormLabel>Title:</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Title"
                        {...field}
                        className={GlobalCssStrings.InputCSS}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              {isSubVersion ? (
                <FormField
                  control={form.control}
                  name="ParentVersionID"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Parent Version:</FormLabel>
                      <FormControl>
                        <Dropdown id={"parentVersion"} {...field} title={"Parent Version"} className={GlobalCssStrings.InputCSS}>
                          {props.versions.filter((v) => v.Title != "1").map((v) => {
                            return { id: Number(v.ID), text: v.Title, value: v.ID };
                          })}
                        </Dropdown>
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <></>
              )}
              <div className="w-full">
                <Button className="w-full mt-4 cursor-pointer" type="submit">
                  {isSubVersion ? "Create Sub-Version" : "Create Version"}
                </Button>
              </div>
            </form>
          </Form>
        </div>
        <DialogFooter></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
